import { useEffect, useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import { Users } from '~/api/scheme';
import { DEFAULT_COLUMNS } from '~/constants/columns';
import useCurrentUser from '../useCurrentUser';

type ActiveColumnsProps = {
  activeColumns: string[];
  onChangeActiveColumns: (column: string[], value: boolean) => void;
  resetToDefault: () => void;
};

type Args = {
  savedColumns?: string[];
  showMaskSettings?: boolean;
  scope: UISettingsScope;
};

const useActiveColumns = (args: Args): ActiveColumnsProps => {
  const { scope, savedColumns, showMaskSettings } = args;

  // Use null state for returning CurrentUser settings from the backend
  const [columns, setColumns] = useState<string[] | null>(null);
  const { currentUser } = useCurrentUser();

  const mutation = useMutation((params: Users.UISettings.UpdateColumnsParams) =>
    Users.UISettings.update(params)
  );

  // TODO: investigate, whether we can use `savedColumns` over `columns`
  // ! It starts work in weird way, need to deep investigation
  const activeColumns = useMemo(
    () => columns || savedColumns || DEFAULT_COLUMNS[scope].default,
    [savedColumns, scope, columns]
  );

  // ! To initiate re-render
  useEffect(() => {
    setColumns(null);
  }, [showMaskSettings]);

  const onChangeActiveColumns = (columns: string[], value: boolean) => {
    setColumns((prevValue) => {
      const newValue = prevValue?.slice() || activeColumns;

      columns.forEach((column) => {
        const index = newValue.indexOf(column);
        if (value) {
          if (index < 0) {
            newValue?.push(column);
          }
        } else {
          if (newValue.includes(column)) {
            if (index >= 0) {
              newValue.splice(index, 1);
            }
          }
        }
      });

      mutation.mutate({
        user_id: Number(currentUser?.id),
        update_mask: Boolean(showMaskSettings),
        ui_settings: { [scope]: { columns: newValue } },
      });

      return newValue;
    });
  };

  const resetToDefault = () => {
    const defaultColoumns = DEFAULT_COLUMNS[scope].default;

    mutation.mutate(
      {
        user_id: Number(currentUser?.id),
        update_mask: Boolean(showMaskSettings),
        ui_settings: { [scope]: { columns: defaultColoumns } },
      },
      {
        onSuccess(data) {
          setColumns(data.data.data.attributes.ui_settings[scope].columns);
        },
      }
    );
  };

  return {
    activeColumns,
    onChangeActiveColumns,
    resetToDefault,
  };
};

export default useActiveColumns;
